
import { defineStore } from "pinia";
import { computed, readonly, ref } from "vue";
import { useLayoutStore } from "./layout";
import logger from "@/logger";
import { useWidgetStore } from "./widget";
import * as storage from '@/common/storage.js';
import signals from "@/common/signals";
import { useMoinBot } from "@/services/bot/botv2";
import type { Intent } from "@/types/Intent";
import type { Teaser } from "@/types/Teaser";
import type { Botconfig } from "@/types";
import { getTeaser } from "@/services/configurations";



const moinBot = useMoinBot();

export const useTeaserStore = defineStore('teaser', () => {
  const layout = useLayoutStore();
  const widgetStore = useWidgetStore();

  const _showTeaser = ref(false);
  const dontShowClosedTeaserFor = ref(0);


  const teaser = ref<Teaser>({
    showAvatar: false,
    message: '',
    qr: [] as Intent[],
    timeout: 0,
    forceShow: false,
  });


  function setTeaser(newteaser: Teaser) {
    if (!newteaser) return;
    // set defaults for timeout and forceShow
    const {
      timeout = 0,
      forceShow = false
    } = newteaser;

    if (moinBot.hasJWTToken() && !forceShow) {
      console.warn('active session - no teaser allowed');
      return;
    }


    const lastClosedValue = storage.readAsNumber('moinai_teaser_closeaction')
    const closedBefore = lastClosedValue ? lastClosedValue : null;

    if (
      closedBefore && // have the teaser closed before
      !forceShow && // if it's forced, we show it anyway
      dontShowClosedTeaserFor.value && // is there a timeout for the teaser supression set?
      Date.now() - closedBefore < dontShowClosedTeaserFor.value
    ) {
      console.warn('teaser was closed before - no teaser is shown');
      return;
    }

    // If we have a timeout it will be set after given time
    // otherwise it will be set imeediately with timeout is default 0
    setTimeout(() => {
      teaser.value = newteaser;
      // If the widget was opened in the meantime don't show teaser
      // otherwise it would show up after closing the widget
      // the getJWT Token guard might not work propperly
      // when the widget is openend instantly adn the connection is
      // not established / we get the first message with the token
      // this might not be the "best" solution but should work as a hotfix
      if (!widgetStore.isWidgetOpen) {
        _showTeaser.value = true;
      }
    }, timeout);
  }

  /**
   * fetch teaser from api based on the current bot and channel isMobile and originUrl
   * @param {string} botId
   * @param {string} channelId
   */
  function fetchRemoteTeaserFromAPI(botId: string, channelId: string) {
    getTeaser(botId, channelId, layout.isMobile)
      .then((data) => {
        if (data) {
          setTeaser(data);
        } else {

          logger.warn('no remote teaser available');
        }
      })
      .catch((error) => logger.error('error fetching teaser', error));
  }

  function handleQuickReplyClick(intent: Intent) {
    _showTeaser.value = false;
    widgetStore.openWidget(intent);
    signals.interaction('teaser', { action: 'button_click', text: intent });
  }

  /**
   * close the teaser
   */
  function handleCloseTeaserClick() {
    _showTeaser.value = false;
    storage.write('moinai_teaser_closeaction', Date.now().toString());
    signals.interaction('teaser', { action: 'close_teaser' });
  }

  const showTeaser = computed(() =>
    _showTeaser.value && !widgetStore.isWidgetOpen
  );

  const hasActiveTeaser = computed(() => _showTeaser.value);


  const showAvatar = computed(() =>
    _showTeaser.value && !widgetStore.isWidgetOpen && teaser.value.showAvatar
  );

  const hideTeaser = () => {
    _showTeaser.value = false;
  }


  const initializeFromConfig = (config: Botconfig) => {
    if (config.dontShowClosedTeaserFor) {
      dontShowClosedTeaserFor.value = config.dontShowClosedTeaserFor;
    }

    if (config.teaser) {
      setTeaser(config.teaser);
    }
  }

  return {
    teaser: readonly(teaser),
    showTeaser,
    showAvatar,
    fetchRemoteTeaserFromAPI,
    initializeFromConfig,
    setTeaser,
    hideTeaser,
    hasActiveTeaser,
    handleQuickReplyClick,
    handleCloseTeaserClick,
  }
})

